import { createPortal } from "react-dom";
import { Mail, MailCheck } from "lucide-react";

export type InvoiceMailSendingOverlayStatus = "sending" | "success";

const APP_THEME_COLOR = "#ff9d00";

interface InvoiceMailSendingOverlayProps {
  open: boolean;
  status: InvoiceMailSendingOverlayStatus;
}

const InvoiceMailSendingOverlay = ({
  open,
  status,
}: InvoiceMailSendingOverlayProps) => {
  if (!open || typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 px-6 backdrop-blur-sm"
      role="alertdialog"
      aria-live="assertive"
      aria-busy={status === "sending"}
      aria-label={
        status === "sending" ? "Sending email" : "Email sent successfully"
      }
    >
      <div className="flex max-w-md flex-col items-center gap-5 text-center">
        {status === "sending" ? (
          <>
            <div className="relative flex h-20 w-20 items-center justify-center">
              <div
                className="absolute inset-0 animate-spin rounded-full border-4"
                style={{
                  borderColor: `${APP_THEME_COLOR}40`,
                  borderTopColor: APP_THEME_COLOR,
                }}
                aria-hidden
              />
              <Mail
                className="relative h-8 w-8"
                style={{ color: APP_THEME_COLOR }}
                strokeWidth={2}
                aria-hidden
              />
            </div>
            <div className="space-y-2">
              <p
                className="text-2xl font-semibold tracking-tight"
                style={{ color: APP_THEME_COLOR }}
              >
                Sending email...
              </p>
              <p className="text-sm leading-relaxed text-gray-300">
                Please do not close this window or refresh the page.
              </p>
            </div>
          </>
        ) : (
          <>
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-green-500/15 ring-1 ring-green-500/30">
              <MailCheck
                className="h-10 w-10 text-green-600"
                strokeWidth={2}
                aria-hidden
              />
            </div>
            <div className="space-y-2">
              <p className="text-2xl font-semibold tracking-tight text-green-500">
                Email sent successfully
              </p>
              <p className="text-sm leading-relaxed text-gray-300">
                The invoice has been mailed to the client.
              </p>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

export default InvoiceMailSendingOverlay;
